import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CommonDialog from 'components/CommonDialog';

const FEE_MODE = {
  UCR: 'ucr',
  CUSTOM: 'custom',
};

const useStyles = makeStyles(() => ({
  feeScheduleSubtitle: {
    marginRight: 8,
    marginTop: 8,
  },
  customFeeFormControl: {
    marginRight: 0,
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-label': {
      width: '100%',
    },
  },
  inlineFeeIdInput: {
    width: 140,
    marginRight: 8,
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
    },
  },
  customFeeLabelRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  customFeeLabelText: {
    marginRight: 8,
  },
}));

const getConfirmMessage = (planName, locationName) => {
  const planLabel = planName.trim();
  const locationSuffix = locationName?.trim();

  const locationPart = locationSuffix ? ` for ${locationSuffix}` : '';

  return `Are you sure you want to sync ${planLabel} plan to PMS${locationPart}?`;
};

const SyncPmsConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  planName,
  locationName,
  forceUcrFeeSchedule,
}) => {
  const classes = useStyles();
  const [feeMode, setFeeMode] = useState(FEE_MODE.UCR);
  const [customFeeScheduleId, setCustomFeeScheduleId] = useState('');

  useEffect(() => {
    if (open) {
      setFeeMode(FEE_MODE.UCR);
      setCustomFeeScheduleId('');
    }
  }, [open]);

  const confirmDisabled = useMemo(
    () =>
      isLoading ||
      (!forceUcrFeeSchedule &&
        feeMode === FEE_MODE.CUSTOM &&
        !customFeeScheduleId.trim()),
    [isLoading, feeMode, customFeeScheduleId, forceUcrFeeSchedule],
  );

  const handleConfirm = async () => {
    if (confirmDisabled) return;

    const extraQuery =
      feeMode === FEE_MODE.CUSTOM
        ? { pms_fees_schedule_id: customFeeScheduleId.trim() }
        : {};
    await onConfirm(extraQuery);
  };

  return (
    <CommonDialog
      open={open}
      handleClose={onClose}
      dialogTitle="Sync to PMS"
      primaryBtnTxt="Confirm"
      secondaryBtnTxt="Cancel"
      maxWidth="sm"
      handlePrimaryButtonClick={handleConfirm}
      disabledPrimaryButton={confirmDisabled}
      isLoading={isLoading}
      disableSecondaryButton={isLoading}
      primaryButtonProps={{ fullWidth: true }}
      secondaryButtonProps={{ fullWidth: true }}
    >
      <Grid container spacing={2} direction="column">
        <Grid item xs={12}>
          <Typography variant="body1" component="p">
            {getConfirmMessage(planName, locationName)}
          </Typography>
        </Grid>
        {!forceUcrFeeSchedule && (
          <Grid item xs={12}>
            <Grid container spacing={0} alignItems="flex-start">
              <Grid item className={classes.feeScheduleSubtitle}>
                <Typography variant="body1" component="p">
                  Select fees schedule for plan:
                </Typography>
              </Grid>
              <Grid item>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="sync-pms-fees-schedule-type"
                    name="sync-pms-fees-schedule-type"
                    value={feeMode}
                    onChange={(e) => setFeeMode(e.target.value)}
                  >
                    <FormControlLabel
                      value={FEE_MODE.UCR}
                      control={
                        <Radio
                          color="secondary"
                          inputProps={{ 'data-testid': 'sync-pms-fee-ucr' }}
                        />
                      }
                      label="UCR fees schedule"
                    />
                    <FormControlLabel
                      value={FEE_MODE.CUSTOM}
                      className={classes.customFeeFormControl}
                      control={
                        <Radio
                          color="secondary"
                          inputProps={{ 'data-testid': 'sync-pms-fee-custom' }}
                        />
                      }
                      label={
                        <span className={classes.customFeeLabelRow}>
                          <Typography
                            variant="body1"
                            component="span"
                            className={classes.customFeeLabelText}
                          >
                            Custom fees schedule
                          </Typography>
                          <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Fees Schedule ID"
                            value={customFeeScheduleId}
                            onChange={(e) =>
                              setCustomFeeScheduleId(e.target.value)
                            }
                            disabled={feeMode !== FEE_MODE.CUSTOM}
                            required={feeMode === FEE_MODE.CUSTOM}
                            className={classes.inlineFeeIdInput}
                            inputProps={{
                              'data-testid':
                                'sync-pms-custom-fee-schedule-id',
                            }}
                          />
                        </span>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </CommonDialog>
  );
};

SyncPmsConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  planName: PropTypes.string.isRequired,
  locationName: PropTypes.string,
  forceUcrFeeSchedule: PropTypes.bool,
};

SyncPmsConfirmDialog.defaultProps = {
  isLoading: false,
  locationName: '',
  forceUcrFeeSchedule: false,
};

export default SyncPmsConfirmDialog;
